import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FiMaximize2, FiMinimize2 } from 'react-icons/fi'
import TableItems from '../Table/TableItems'
import Search from '../Table/Search'
import { OrganizationUser } from '../../interfaces/organization'
import { CsvColumn } from '../../utils/toCsv'
import { filterByColumns } from '../../utils/tableSearch'

interface Props {
  users: OrganizationUser[]
}

const fullName = (user: OrganizationUser) =>
  `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim()

const OrganizationDetailsUsers = ({ users = [] }: Props) => {
  const { t } = useTranslation()
  const [search, setSearch] = useState('')
  const [isExpanded, setIsExpanded] = useState(false)

  const searchColumns: CsvColumn<OrganizationUser>[] = useMemo(
    () => [
      { header: 'ID', value: (row) => row.id },
      { header: t('fullName'), value: (row) => fullName(row) },
      { header: t('email'), value: (row) => row.email ?? '' },
      { header: t('position'), value: (row) => row.position ?? '' },
    ],
    [t],
  )

  const rows = useMemo(
    () => filterByColumns(searchColumns, users, search),
    [searchColumns, users, search],
  )

  const columns = [
    {
      Header: 'ID',
      accessor: 'id',
    },
    {
      Header: t('fullName'),
      accessor: (item: OrganizationUser) => fullName(item),
      Cell: ({ value }: { value: string }) =>
        value ? <span className='text-black/80'>{value}</span> : '-',
    },
    {
      Header: t('email'),
      accessor: 'email',
      Cell: ({ value: email }: { value: OrganizationUser['email'] }) =>
        email ? (
          <a className='hover:underline' href={`mailto:${email}`}>
            {email}
          </a>
        ) : (
          '-'
        ),
    },
    {
      Header: t('position'),
      accessor: 'position',
      Cell: ({ value: position }: { value: OrganizationUser['position'] }) =>
        position ? <span>{t(position)}</span> : '-',
    },
  ]

  return (
    <div className='w-full'>
      <div className='flex items-center justify-between gap-5 flex-wrap mb-7'>
        <div className='flex items-center gap-2 font-bold text-[24px]'>
          <h2>{t('users')}</h2>
          <p>({users?.length ?? 0})</p>
        </div>
        <div className='flex items-center gap-3'>
          <Search
            filter={search}
            setFilter={(value) => setSearch(value ?? '')}
            className='static top-auto right-auto md:right-auto'
          />
          <button
            type='button'
            onClick={() => setIsExpanded((previous) => !previous)}
            aria-label={t(isExpanded ? 'collapse' : 'expand')}
            title={t(isExpanded ? 'collapse' : 'expand')}
            className='text-lightGray hover:text-primaryBlue transition-colors p-2'
          >
            {isExpanded ? (
              <FiMinimize2 className='text-xl' />
            ) : (
              <FiMaximize2 className='text-xl' />
            )}
          </button>
        </div>
      </div>
      <TableItems
        columns={columns}
        data={rows}
        noDataFoundMessage='noOrganizationsUsersFound'
        showLastColumn={false}
        showSearch={false}
        pageSize={isExpanded ? 10 : 5}
        tableHeightClassName={isExpanded ? 'h-[820px]' : 'h-[420px]'}
      />
    </div>
  )
}

export default OrganizationDetailsUsers
